import { useEffect, useMemo, useState } from 'react';
import StatusInfoBox from '../../../components/core/StatusInfoBox/StatusInfoBox';
import useCartWorkflowWizardStyles from './CartWorkflowWizard.styles';
import Page from '../../../components/core/Page/Page';
import ViaeroButton from '../../../components/core/ViaeroButton/ViaeroButton';
import { useCart } from '../../../contexts/CartContext/CartContext.hooks';
import SummaryCard from '../SummaryCard/SummaryCard';
import CheckoutProgressBar from '../CheckoutProgressBar/CheckoutProgressBar';
import { usePaymentStatus } from 'contexts/PaymentContext/PaymentContext.hooks';
import SavingsSummary from '../SummaryCard/SavingsSummary/SavingsSummary';
import LoadingWheel from '../../../components/core/LoadingWheel/LoadingWheel';
import { ICartWorkflowWizardStep } from './CartWorkflowWizard.types';
const loadPdfJs = () => import('react-pdf');

export interface ICartWorkflowWizardProps {
    steps: ICartWorkflowWizardStep[];
    title: string;
}

const CartWorkflowWizard = ({ steps, title }: ICartWorkflowWizardProps) => {
    const styles = useCartWorkflowWizardStyles();

    const { cart, setCart, cartStepIndex, updateCartStepIndex } = useCart();
    const { status } = usePaymentStatus();

    const [stepIndex, setStepIndex] = useState(0);

    const [statusMessage, setStatusMessage] = useState('');

    const [dataLoading, setDataLoading] = useState(false);

    const statusInfoBox = useMemo(() => {
        return statusMessage ? (
            <StatusInfoBox
                isSuccessful={false}
                text={statusMessage}
                className={styles.statusInfoBox}
            />
        ) : (
            <></>
        );
    }, [statusMessage]);

    const initializePdfJs = async () => {
        await loadPdfJs();
        const { pdfjs } = await import('react-pdf');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/build/pdf.worker.min.js',
            import.meta.url
        ).toString();
    };

    initializePdfJs().catch(console.error);

    const handleClick = () => {
        const step = steps[stepIndex];
        const validationResults = step.validationHook
            ? step.validationHook(cart, status)
            : { isValid: true, messages: [] };

        if (validationResults.isValid) {
            setStatusMessage('');

            if (step.onProceed) {
                setDataLoading(true);
                step.onProceed(
                    cart,
                    () => {
                        setDataLoading(false);
                        setCart(cart);
                        updateCartStepIndex(stepIndex + 1);
                    },
                    () => console.log('idi call failed')
                );
            } else {
                updateCartStepIndex(stepIndex + 1);
            }
        } else {
            if (validationResults.messages.length < 1) return;
            setStatusMessage(validationResults.messages[0]);
        }
    };

    useEffect(() => {
        setStepIndex(cartStepIndex);
        scrollTo(0, 0);
    }, [cartStepIndex]);

    return (
        <Page
            title={`Viaero Wireless: ${steps[stepIndex].title ?? 'Shopping Cart'}`}
            description=''
            keywords=''
        >
            <div className={styles.wizardContainer}>
                <h1 className={styles.securedCheckoutHeader}>{title}</h1>

                <div className={styles.progressBar}>
                    <CheckoutProgressBar stepIndex={stepIndex} steps={steps} />
                </div>

                <div
                    className={styles.root}
                    {...(steps[stepIndex].wrapped ? { class: 'wrapped' } : {})}
                >
                    <div className={styles.stepComponentWrapper}>{steps[stepIndex].component}</div>

                    <div className={styles.summaryWrapper}>
                        <div className={styles.sticky}>
                            {!steps[stepIndex].hideSummary && (
                                <div className={styles.summaryCards}>
                                    <SummaryCard />

                                    <SavingsSummary />
                                </div>
                            )}

                            {steps[stepIndex].proceedText &&
                                !dataLoading &&
                                !cart.isDuplicateCustomer && (
                                    <ViaeroButton
                                        click={handleClick}
                                        text={steps[stepIndex].proceedText ?? ''}
                                        testId={'wizard-proceed-button'}
                                        disabled={stepIndex === steps.length - 1 || dataLoading}
                                        className={styles.proceedButton}
                                    />
                                )}

                            {dataLoading && <LoadingWheel className={styles.loadingWheel} />}

                            {statusInfoBox}
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default CartWorkflowWizard;
